"use client";

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ModelViewContainer from '@/components/models/ModelViewContainer';
import { useWallet } from '@/contexts/WalletContext';

const ModelViewer: React.FC = () => {
  const [fileId, setFileId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Используем контекст кошелька
  const { walletAddress, isConnected, connectWallet } = useWallet();
  
  console.log('ModelViewer: Инициализация компонента');
  
  // Функция для загрузки файла, обернутая в useCallback
  const uploadFile = useCallback(async (file: File) => {
    console.log('ModelViewer: Начало загрузки файла');
    console.log('ModelViewer: Имя файла:', file.name);
    console.log('ModelViewer: Размер файла:', file.size, 'байт');
    console.log('ModelViewer: Тип файла:', file.type);
    
    // Проверяем подключение кошелька
    if (!isConnected) {
      console.log('ModelViewer: Кошелек не подключен, подключаем...');
      
      try {
        // Подключаем кошелек
        const address = await connectWallet();
        if (!address) {
          console.error('ModelViewer: Не удалось подключить кошелек');
          setError('Не удалось подключить кошелек. Пожалуйста, попробуйте снова.');
          return;
        }
        console.log('ModelViewer: Кошелек успешно подключен, адрес:', address);
      } catch (error) {
        console.error('ModelViewer: Ошибка при подключении кошелька:', error);
        setError('Ошибка при подключении кошелька. Пожалуйста, попробуйте снова.');
        return;
      }
    }
    
    // Проверяем тип файла
    if (!file.name.toLowerCase().endsWith('.fbx')) {
      console.error('ModelViewer: Неподдерживаемый тип файла');
      setError('Поддерживаются только файлы FBX.');
      return;
    }
    
    // Проверяем размер файла (максимум 50 МБ)
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 МБ в байтах
    if (file.size > MAX_FILE_SIZE) {
      console.error('ModelViewer: Файл слишком большой');
      setError('Размер файла не должен превышать 50 МБ.');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    
    try {
      console.log('ModelViewer: Создание FormData для загрузки');
      const formData = new FormData();
      formData.append('file', file);
      
      // Добавляем адрес кошелька в запрос
      if (walletAddress) {
        formData.append('walletAddress', walletAddress);
        console.log('ModelViewer: Добавлен адрес кошелька:', walletAddress);
      } else {
        console.error('ModelViewer: Адрес кошелька отсутствует');
        throw new Error('Адрес кошелька отсутствует. Пожалуйста, подключите кошелек.');
      }
      
      // Подробное логирование запроса для отладки
      console.log('ModelViewer: Отправка запроса на загрузку');
      console.log('ModelViewer: URL:', '/api/upload?debug=true');
      console.log('ModelViewer: Метод:', 'POST');
      console.log('ModelViewer: Содержимое FormData:', {
        file: {
          name: file.name,
          size: file.size,
          type: file.type
        },
        walletAddress
      });
      
      // Выполнение запроса с исправленными заголовками
      const response = await fetch('/api/upload?debug=true', {
        method: 'POST',
        body: formData,
        // Не устанавливаем Content-Type явно, чтобы браузер сам добавил boundary
        // для multipart/form-data
        headers: {
          // Добавляем дополнительные заголовки, которые помогут совместимости
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      // Подробное логирование ответа
      console.log('ModelViewer: Статус ответа:', response.status);
      console.log('ModelViewer: Статус-текст:', response.statusText);
      console.log('ModelViewer: Заголовки ответа:', Object.fromEntries([...response.headers]));
      
      // Пытаемся получить тело ответа
      const responseText = await response.text();
      console.log('ModelViewer: Тело ответа:', responseText);
      
      // Конвертируем текст ответа в JSON, если возможно
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('ModelViewer: Ответ от сервера (JSON):', data);
      } catch (e) {
        console.error('ModelViewer: Ошибка парсинга JSON ответа:', e);
        // Возвращаемся к началу для дальнейшей обработки
        if (!response.ok) {
          throw new Error(`Ошибка при загрузке файла: ${response.status} ${response.statusText}\nТело ответа: ${responseText}`);
        }
        throw new Error(`Ошибка парсинга ответа: ${e}\nТело ответа: ${responseText}`);
      }
      
      if (!response.ok) {
        console.error('ModelViewer: Ошибка при загрузке файла, статус:', response.status);
        let errorMessage = 'Ошибка при загрузке файла';
        
        if (data && data.error) {
          errorMessage = data.error;
          console.error('ModelViewer: Детали ошибки:', data);
        }
        
        throw new Error(errorMessage);
      }
      
      if (data.fileId) {
        console.log('ModelViewer: Файл успешно загружен, fileId =', data.fileId);
        setFileId(data.fileId);
      } else {
        console.error('ModelViewer: Отсутствует fileId в ответе');
        throw new Error('Ошибка при загрузке файла: отсутствует ID файла');
      }
    } catch (error) {
      console.error('ModelViewer: Ошибка при загрузке файла:', error);
      setError(error instanceof Error ? error.message : 'Неизвестная ошибка при загрузке файла');
    } finally {
      setIsUploading(false);
    }
  }, [isConnected, connectWallet, walletAddress, setFileId, setError, setIsUploading]);
  
  // Обработчик для drag-and-drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    console.log('ModelViewer: Файлы перетащены:', acceptedFiles.length);
    
    if (acceptedFiles.length === 0) {
      console.error('ModelViewer: Нет принятых файлов');
      setError('Пожалуйста, выберите файл FBX.');
      return;
    }
    
    const file = acceptedFiles[0];
    console.log('ModelViewer: Выбран файл:', file.name);
    
    uploadFile(file);
  }, [uploadFile]);
  
  // Настройка dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/octet-stream': ['.fbx'],
      'model/fbx': ['.fbx'],
    },
    maxFiles: 1,
  });
  
  return (
    <div className="container mx-auto py-8 px-4">
      {/* Изменяем структуру сетки: Drag and Drop сверху на всю ширину, область просмотра по центру */}
      <div className="flex flex-col w-full gap-8">
        {/* Секция загрузки модели - растянута на всю ширину */}
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Загрузить модель</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'
              }`}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <p className="text-gray-500">Загрузка...</p>
              ) : isDragActive ? (
                <p className="text-primary">Перетащите файл сюда...</p>
              ) : (
                <div>
                  <p className="text-gray-500 mb-2">Перетащите FBX файл сюда или нажмите для выбора</p>
                  <p className="text-xs text-gray-400">Поддерживаются только файлы FBX (макс. 50 МБ)</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Область просмотра модели - занимает всю доступную ширину */}
        <div className="w-full">
          <ModelViewContainer fileId={fileId} />
        </div>
      </div>
    </div>
  );
};

export default ModelViewer; 